/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
  TOKEN_EXPIRED_OR_INVALID_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Requirements List logic module.
 * Handles requirements listing operations including user role verification
 * and fetching active requirements for display.
 * @module logic/requirementsList
 */

import { refreshToken, signOut } from "../../utils/common.js";

const MAX_HOME_REQUIREMENTS = 3;

/**
 * Fetches and sets user role/permission data.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @param {Function} changePermissions - Callback to update permissions state
 * @returns {Promise<void>}
 */
export const getUserRoleData = async (userId, navigate, changePermissions) => {
  const tokenToUse = localStorage.getItem("token");
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${tokenToUse}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission/${userId}`,
      options,
    );
    const roleData = await response.json();
    const isTokenExpired =
      roleData?.message === TOKEN_EXPIRED_MESSAGE ||
      roleData?.message === TOKEN_EXPIRED_OR_INVALID_MESSAGE;
    if (isTokenExpired) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      await getUserRoleData(userId, navigate, changePermissions);
      return;
    }
    if (response.ok && roleData?.permisos != null) {
      changePermissions(roleData.permisos);
    }
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
  }
};

/**
 * Fetches active requirements with future deadlines, sorted by creation date.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of up to 3 most recent active requirements
 */
export const getRequirements = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}requirement/user/${userId}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getRequirements(userId, navigate);
    }
    if (Array.isArray(data)) {
      const currentDate = new Date();
      const activeRequirements = data.filter(
        (requirement) => requirement.activo === true,
      );
      const futureRequirements = activeRequirements.filter((requirement) => {
        const requirementDate = new Date(requirement.fechaLimite);
        return requirementDate >= currentDate;
      });
      const sortedRequirements = [...futureRequirements].sort(
        (a, b) => new Date(b.fechaDeCreacion) - new Date(a.fechaDeCreacion),
      );
      return sortedRequirements.slice(0, MAX_HOME_REQUIREMENTS);
    }
    return [];
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

export const getAllValidRequirements = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}requirement/user/${userId}`,
      options,
    );
    const data = await response.json();
    logger.info("getRequirements -> data", { data: data });
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getAllValidRequirements(userId, navigate);
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches requirements from a user's single company.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of requirements
 */
export const getRequirementsFromSingleCompany = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}requirement/company/${userId}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getRequirementsFromSingleCompany(userId, navigate);
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches unapproved requirements from a user's single company.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of unapproved requirements
 */
export const getCompanyUnapprovedRequirements = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}requirement/company/${userId}/unapproved`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getCompanyUnapprovedRequirements(userId, navigate);
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

export const updateRequirement = async (
  requirementId,
  updatedData,
  navigate,
) => {
  const token = localStorage.getItem("token");
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(updatedData),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}requirement/status/${requirementId}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      await updateRequirement(requirementId, updatedData, navigate);
      return;
    }
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
  }
};

export const approveRequirement = async (requirementId, userId, navigate) => {
  const token = localStorage.getItem("token");
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      userId,
    }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}requirement/${requirementId}/approve`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await approveRequirement(requirementId, userId, navigate);
    }

    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

export const deleteRequirement = async (
  requirementId,
  rejectionReason,
  navigate,
) => {
  const token = localStorage.getItem("token");
  const options = {
    method: "DELETE",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      message: rejectionReason,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}requirement/${requirementId}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await deleteRequirement(requirementId, rejectionReason, navigate);
    }

    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};
